/**
 * Payer Connections — visual tokens for the five fold states and the four
 * blocked-on parties. Values are from the design handoff
 * (design_handoff_enrollment_dashboard/README.md, "Design tokens" table),
 * which the PRD's fold rule (section 4, AC-3/AC-4) matches exactly.
 *
 * `@jotpsych/ui`'s Chip `soft` variant only exposes MUI's six palette
 * colors, and this palette (five distinct hues incl. a dark "live" chip)
 * doesn't map onto them 1:1 — so state chips apply these hex values via
 * `sx` on top of the shared Chip primitive rather than picking a `color`.
 */

import type { BlockedParty, PayerAudience, PayerState } from './types';

export interface StateTokens {
  label: string;
  bg: string;
  border: string;
  text: string;
}

/**
 * Payer-state chip tokens, including `live` (used on the roster's collapsed
 * count, not on a row). Colors are the Payer Handoff design's four tones:
 * indigo = ours, amber = needs the customer, slate = with a third party or
 * terminal, green = done. The state model itself is unchanged; `ready` keeps
 * its own label because the fold keeps it distinct from `live`.
 */
export const STATE_TOKENS: Record<PayerState, StateTokens> = {
  needs_us: { label: 'On JotPsych', bg: '#f0f0fa', border: '#cdcdec', text: '#302a9e' },
  needs_customer: { label: 'Needs you', bg: '#fff5eb', border: '#f2cfa8', text: '#a8480b' },
  waiting: { label: 'With the payer', bg: '#f2f2f7', border: '#dcdce6', text: '#4e4e63' },
  ready: { label: 'Ready to bill', bg: '#eaf6ee', border: '#c2e2cd', text: '#15803d' },
  canceled: { label: 'Canceled', bg: '#f2f2f7', border: '#dcdce6', text: '#4e4e63' },
  live: { label: 'Live', bg: '#eaf6ee', border: '#c2e2cd', text: '#15803d' },
};

/**
 * Which party holds a payer, for the table's "Who has it" column. The design
 * names five parties; this app has four — it has no separate clearinghouse
 * party, so a rail sitting with the clearinghouse folds to `waiting` like any
 * other third party. `ready` and `live` are nobody's blocker.
 */
export const STATE_PARTY: Record<PayerState, BlockedParty> = {
  needs_us: 'JOTPSYCH',
  needs_customer: 'CUSTOMER',
  waiting: 'PAYER',
  ready: 'NONE',
  canceled: 'NONE',
  live: 'NONE',
};

/** Blocked-on party chip color, using the Chip primitive's palette (AC-14). */
export const PARTY_CHIP_COLOR: Record<BlockedParty, 'error' | 'warning' | 'info' | 'success'> = {
  JOTPSYCH: 'error',
  CUSTOMER: 'warning',
  PAYER: 'info',
  NONE: 'success',
};

/** Row and summary badges share the same audience-specific label. */
export function payerStateLabel(state: PayerState, audience: PayerAudience = 'clinic'): string {
  return state === 'needs_customer' && audience === 'staff'
    ? 'Needs clinic'
    : STATE_TOKENS[state].label;
}
